/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
/**
 * Single shared entry point for all timezone-aware date/time conversion.
 *
 * Design rule: no component may call `new Date(x).toLocaleString(...)`,
 * `.toISOString()`, or the `Intl` API directly — every conversion goes
 * through `toUtc()` (outbound) or `toLocal()` (inbound/display) below, so a
 * future timezone bug is fixable in one place instead of N places.
 */

/**
 * The single shared timezone resolver — always the browser's own reported
 * zone, never the JS runtime's implicit/system default.
 */
export function getReferenceTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The one core outbound function. Converts any value `Date` accepts into a
 * UTC ISO-8601 string. Throws on invalid/unparseable input rather than
 * silently producing "Invalid Date".
 */
export function toUtc(input) {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`toUtc: invalid date input: ${input}`);
  }
  return date.toISOString();
}

/**
 * The one core inbound/display function. Parses a UTC ISO-8601 string and
 * formats it in the browser's resolved timezone. Throws on invalid input.
 */
export function toLocal(utcIsoString, formatOptions = {}) {
  const date = new Date(utcIsoString);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`toLocal: invalid date input: ${utcIsoString}`);
  }
  return date.toLocaleString('en-US', {
    ...formatOptions,
    timeZone: getReferenceTimeZone(),
  });
}

/** Thin wrapper: date-only display (e.g. "9/1/2026"). */
export function formatDate(isoString) {
  return toLocal(isoString, { month: 'numeric', day: 'numeric', year: 'numeric' });
}

/** Thin wrapper: date + time display (e.g. "9/1/2026, 2:30 PM"). */
export function formatDateTime(isoString) {
  return toLocal(isoString, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Composed helper for calendar-day filters: given an `<input type="date">`
 * value (e.g. "2026-09-01"), returns the UTC instants for that local day's
 * start and exclusive end, using the browser's resolved timezone.
 */
export function localDayToUtcRange(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) {
    throw new Error(`localDayToUtcRange: invalid date input: ${dateStr}`);
  }
  const start = new Date(year, month - 1, day, 0, 0, 0, 0);
  const end = new Date(year, month - 1, day + 1, 0, 0, 0, 0);
  return { from: toUtc(start), to: toUtc(end) };
}
